import { cn } from "lib/utils";
import React, { useState } from "react";

import searchAppointment from "assets/images/totem/search-appointment.svg";
import { ActionButton } from "../ActionButton";
import { Stepper } from "../Stepper";
import { TotemHeader } from "../TotemHeader";
import { KeyboardKey, TotemKeyboard } from "../TotemKeyboard";

type LookupScreenProps = {
	type: "plate" | "cpf";
	onBack: () => void;
	onSearch: (query: { plate?: string; cpf?: string }) => void;
	loading?: boolean;
};

export const LookupScreen: React.FC<LookupScreenProps> = ({
	type,
	onBack,
	onSearch,
	loading = false,
}) => {
	const isPlate = type === "plate";
	const [value, setValue] = useState("");
	const [isFocused, setIsFocused] = useState(false);

	const handleKeyPress = (key: KeyboardKey) => {
		if (
			!key.value ||
			key.value === "noop" ||
			key.value === "shift" ||
			key.value === "numbers" ||
			key.value === "done"
		)
			return;
		if (key.value === "delete") {
			setValue((current) => current.slice(0, -1));
			return;
		}

		const nextKeyValue = key.value;

		setValue((current) =>
			isPlate
				? `${current}${nextKeyValue}`.slice(0, 7)
				: `${current}${nextKeyValue}`.slice(0, 11),
		);
	};

	const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const nextValue = isPlate
			? event.target.value.replace(/[^a-z0-9]/gi, "").slice(0, 7)
			: event.target.value.replace(/\D/g, "").slice(0, 11);

		setValue(nextValue);
	};

	const handleCpfKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (!isPlate && (event.key === "Backspace" || event.key === "Delete")) {
			event.preventDefault();
			setValue((current) => current.slice(0, -1));
		}
	};

	const displayCpf = value
		? value.replace(
				/(\d{3})(\d{3})(\d{3})(\d{0,2})/,
				(_, a, b, c, d) => `${a}.${b}.${c}-${d}`,
			)
		: "•••.•••.•••-••";

	const handleLookup = () => {
		if (!value || loading) return;
		onSearch(isPlate ? { plate: value.toUpperCase() } : { cpf: value });
	};

	return (
		<main
			className={cn(
				"mx-auto flex min-h-full w-full max-w-[800px] flex-col items-center bg-white px-6 pb-14 pt-8 max-[1023px]:px-5 max-[1023px]:pb-10 max-[1023px]:pt-6 max-[639px]:px-3 max-[639px]:pb-8 max-[639px]:pt-4",
			)}
		>
			<section
				className={cn(
					"relative h-[545px] w-full shrink-0 max-[639px]:h-[475px]",
				)}
			>
				<div
					className={cn(
						"absolute inset-x-0 top-14 flex h-[489px] flex-col items-start gap-[10px] bg-[#F3F4F6] px-14 pt-24 max-[1023px]:top-12 max-[1023px]:px-8 max-[1023px]:pt-20 max-[639px]:top-10 max-[639px]:h-[425px] max-[639px]:px-4 max-[639px]:pt-20",
					)}
				>
					<Stepper />
					<div
						className={cn(
							"flex w-full flex-col items-start gap-[10px] pb-6",
						)}
					>
						<h1
							className={cn(
								"w-full font-sans text-[32px] font-bold leading-normal text-[#1D293D] max-[639px]:text-[24px]",
							)}
						>
							{isPlate ? "Informe a placa" : "Informe seu CPF"}
						</h1>
						<p
							className={cn(
								"w-full font-sans text-[24px] leading-normal text-[#45556C] max-[639px]:text-[17px]",
							)}
						>
							{isPlate
								? "Digite a placa do veículo para continuar."
								: "Digite os 11 números do CPF do motorista."}
						</p>
					</div>
					<div
						className={cn(
							"flex w-full flex-col items-start gap-[10px] bg-white px-6 py-8 max-[639px]:px-4 max-[639px]:py-5",
						)}
					>
						<span
							className={cn(
								"whitespace-nowrap font-GilroySemibold text-[16px] tracking-[0.8px] text-[#6A7282]",
							)}
						>
							{isPlate ? "PLACA DO VEÍCULO" : "CPF DO MOTORISTA"}
						</span>
						{isPlate ? (
							<input
								aria-label="Placa do veículo"
								value={value}
								onChange={handleInputChange}
								placeholder={isFocused ? "" : "ABC1D23"}
								onFocus={() => setIsFocused(true)}
								onBlur={() => setIsFocused(false)}
								maxLength={7}
								inputMode="text"
								className={cn(
									"h-11 min-w-full bg-transparent font-sans text-[36px] font-semibold leading-normal text-[#4B5563] outline-none placeholder:text-[#4B5563] max-[639px]:text-[28px]",
								)}
							/>
						) : (
							<input
								aria-label="CPF do motorista"
								value={value ? displayCpf : ""}
								placeholder={isFocused ? "" : "•••.•••.•••-••"}
								onFocus={() => setIsFocused(true)}
								onBlur={() => setIsFocused(false)}
								onKeyDown={handleCpfKeyDown}
								onChange={handleInputChange}
								inputMode="numeric"
								maxLength={14}
								className={cn(
									"h-11 min-w-full bg-transparent font-sans text-[36px] font-semibold leading-normal text-[#4B5563] outline-none placeholder:text-[#4B5563] max-[639px]:text-[25px]",
								)}
							/>
						)}
					</div>
				</div>
				<TotemHeader />
			</section>
			<section className={cn("flex w-full flex-col gap-8")}>
				<TotemKeyboard
					type={isPlate ? "alphanumeric" : "numeric"}
					onKeyPress={handleKeyPress}
				/>
				<div
					className={cn(
						"flex w-full flex-col gap-14 max-[639px]:gap-8",
					)}
				>
					<ActionButton
						iconSrc={searchAppointment}
						onClick={handleLookup}
						loading={loading}
						loadingText="Buscando..."
					>
						Buscar agendamento
					</ActionButton>
					<ActionButton
						variant="secondary"
						direction="back"
						onClick={onBack}
					>
						Alterar identificação
					</ActionButton>
				</div>
			</section>
		</main>
	);
};
